import { TfvcChangeset } from '../../core/interfaces/job.interface';
import { GitFileRevision } from '../../core/interfaces/git.interface';

/**
 * Diz se a versão revisada de um arquivo ainda é a vigente na origem (TFVC ou git).
 *
 * "Recente" por idade não responde o que o dev quer saber: um changeset de seis meses pode
 * ser a versão atual, e um de ontem pode já ter sido sobrescrito. A pergunta é "esse review
 * ainda vale para o fonte de hoje?", e quem responde é o histórico do arquivo.
 */
export interface ChangesetFreshness {
  state: 'current' | 'outdated' | 'checking';
  /**
   * Curto de propósito: o po-tag passa a mostrar o próprio texto num tooltip quando
   * passa de 155px, e o selo ficaria com dois balões. A frase completa vai no tooltip.
   */
  label: string;
  tooltip: string;
  /** Como a versão se chama na origem: changeset no TFVC, commit no git. Ausente enquanto verifica. */
  unit?: 'changeset' | 'commit';
  /** A versão que o review viu. Ausente enquanto verifica. */
  reviewed?: FreshnessChange;
  /** Resumo do atraso, para o cabeçalho do modal. Só quando desatualizado. */
  summary?: string;
  /**
   * O que mudou no arquivo depois dela, do mais novo para o mais antigo. Só quando
   * desatualizado; é o que o selo abre ao ser clicado.
   */
  newer?: FreshnessChange[];
}

/** Um changeset ou commit, pronto para listar. */
export interface FreshnessChange {
  /** Número do changeset no TFVC (`680926`), SHA curto no git. */
  id: string;
  /** `07/10/2026`; vazio quando a origem não informou a data. */
  when: string;
  author: string;
  message: string;
}

/**
 * O selo enquanto o histórico carrega. Ocupa o lugar do definitivo para a barra não pular
 * quando a resposta chega — a consulta à origem pode levar alguns segundos, e segurar o
 * diff por ela atrasaria o que importa por uma informação secundária.
 */
export const CHECKING_FRESHNESS: ChangesetFreshness = {
  state: 'checking',
  label: 'Verificando…',
  tooltip: 'Consultando o histórico do arquivo na origem.'
};

/**
 * Monta o selo a partir da primeira página do histórico, que a API pede do mais novo para o
 * mais antigo. Dentro da página a conta não depende dessa ordem: "N depois" é quantos ids
 * são maiores que o revisado, e a lista do que mudou sai ordenada pelo id, do maior ao menor.
 *
 * A referência é o `to`; quando o `to` é local (comparação com upload), é o `from`, contra o
 * qual o arquivo enviado foi comparado. Sem a referência na página, só dá para afirmar algo
 * se houver mais páginas — aí ela está, no mínimo, uma página inteira atrás. Página
 * incompleta sem a referência é dado inconsistente, e o selo some em vez de chutar.
 */
export function buildChangesetFreshness(
  history: TfvcChangeset[],
  from: number | null,
  to: number | null,
  hasMore: boolean
): ChangesetFreshness | null {
  const reference = (to ?? 0) > 0 ? (to as number) : (from ?? 0) > 0 ? (from as number) : 0;
  if (!reference || !history.length) { return null; }

  const entries = history
    .map(cs => ({ id: Number.parseInt(cs.id, 10), cs }))
    .filter(e => Number.isFinite(e.id));
  if (!entries.length) { return null; }

  const entry = entries.find(e => e.id === reference);
  const newer = entries.filter(e => e.id > reference).length;
  const reviewed = entry ? tfvcChange(entry.cs) : { id: String(reference), when: '', author: '', message: '' };

  if (newer === 0) {
    // Nada mais novo, mas a referência fora da lista é dado inconsistente: melhor não afirmar.
    if (!entry) { return null; }
    return {
      state: 'current',
      label: 'Versão atual',
      tooltip: `Sem alterações depois do changeset ${reference}.`,
      unit: 'changeset',
      reviewed
    };
  }
  // A página inteira é mais nova que a referência: há pelo menos isso, talvez mais.
  if (!entry && newer === entries.length && !hasMore) { return null; }

  const behind = !entry && hasMore ? `${newer}+` : `${newer}`;
  const noun = newer === 1 && entry ? 'changeset' : 'changesets';
  const changes = entries
    .filter(e => e.id > reference)
    .sort((a, b) => b.id - a.id)
    .map(e => tfvcChange(e.cs));
  return {
    state: 'outdated',
    label: `Desatualizado (${behind.endsWith('+') ? behind : `+${behind}`})`,
    tooltip: `${behind} ${noun} depois da versão revisada.\nClique para ver o que mudou.`,
    unit: 'changeset',
    reviewed,
    summary: outdatedSummary(changes, behind, noun),
    newer: changes
  };
}

/**
 * O mesmo selo para repositório git, a partir de dois históricos do arquivo.
 *
 * SHA não tem ordem, então não dá para contar "revisões maiores que a revisada". A conta é
 * de identidade: `atReview` é o histórico com `ref` na revisão revisada, e o primeiro item
 * dele é a última versão do arquivo que o review viu — que pode não ser o próprio commit,
 * quando ele não mexeu neste arquivo. `today` é o histórico na branch hoje; a posição
 * daquela versão nele é quantos commits mexeram no arquivo depois.
 *
 * Fora da lista de hoje: se ela veio cheia, a versão está além dela; se não, a revisão não
 * está nesta branch (commit de outra branch, PR com force-push), e o selo some.
 */
export function buildRevisionFreshness(
  atReview: GitFileRevision[],
  today: GitFileRevision[],
  todayHasMore: boolean
): ChangesetFreshness | null {
  const reviewed = atReview[0];
  if (!reviewed?.revision || !today.length) { return null; }

  const index = today.findIndex(r => r.revision === reviewed.revision);
  if (index === 0) {
    const change = gitChange(reviewed);
    return {
      state: 'current',
      label: 'Versão atual',
      tooltip: `Sem alterações depois do commit ${change.id}.`,
      unit: 'commit',
      reviewed: change
    };
  }
  if (index < 0 && !todayHasMore) { return null; }

  const behind = index > 0 ? `${index}` : `${today.length}+`;
  const noun = index === 1 ? 'commit' : 'commits';
  const changes = (index > 0 ? today.slice(0, index) : today).map(r => gitChange(r));
  return {
    state: 'outdated',
    label: `Desatualizado (${behind.endsWith('+') ? behind : `+${behind}`})`,
    tooltip: `${behind} ${noun} depois da versão revisada.\nClique para ver o que mudou.`,
    unit: 'commit',
    reviewed: gitChange(reviewed),
    summary: outdatedSummary(changes, behind, noun),
    newer: changes
  };
}

/**
 * "Desatualizado desde 25/09/2026 · 1 changeset depois". O "desde" é a data da primeira
 * alteração depois da revisada — a mais antiga da lista. Quando a revisada ficou além da
 * página ("20+"), essa primeira alteração não está na lista, e a frase não afirma o "desde".
 */
function outdatedSummary(changes: FreshnessChange[], behind: string, noun: string): string {
  const firstChange = changes[changes.length - 1];
  const since = !behind.endsWith('+') && firstChange?.when ? ` desde ${firstChange.when}` : '';
  return `Desatualizado${since} · ${behind} ${noun} depois`;
}

function tfvcChange(cs: TfvcChangeset): FreshnessChange {
  return { id: cs.id, when: formatDate(cs.checkInDate), author: cs.author ?? '', message: cs.description ?? '' };
}

function gitChange(rev: GitFileRevision): FreshnessChange {
  return {
    id: rev.shortRevision || rev.revision.slice(0, 7),
    when: formatDate(rev.committedAt),
    author: rev.authorName ?? '',
    message: rev.message ?? ''
  };
}

function formatDate(dateText: string | undefined): string {
  const date = dateText ? new Date(dateText) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('pt-BR') : '';
}
